import type { AccountSession } from "@/lib/account-session";
import type { LastReading } from "@/lib/last-reading";

interface AuthSubject {
  id: string;
}

export interface CustomerJourneyIdentity {
  accountSession: AccountSession | null;
  authSubject: AuthSubject | null;
  rememberedReading: LastReading | null;
  hasPrivateAccountAccess: boolean;
  primaryHref: string;
  primaryLabel: "My Jyotishi" | "My Reading";
}

function readingHref(reading: LastReading): string {
  const query = reading.language === "hi" ? "?lang=hi" : "";
  return `/chat/${encodeURIComponent(reading.id)}${query}`;
}

/**
 * Resolve the next relationship surface without conflating access proofs.
 *
 * A signed account capability can open the private account. A remembered
 * reading is only a same-browser resume hint, and Supabase auth is only a
 * verified account subject. Neither localStorage nor a browser-supplied
 * email/phone is promoted into cross-reading ownership here (ADR-0038).
 */
export function resolveCustomerJourneyIdentity({
  accountSession,
  authSubject,
  rememberedReading,
}: {
  accountSession: AccountSession | null;
  authSubject: AuthSubject | null;
  rememberedReading: LastReading | null;
}): CustomerJourneyIdentity {
  if (accountSession) {
    return {
      accountSession,
      authSubject,
      rememberedReading,
      hasPrivateAccountAccess: true,
      primaryHref: "/account/",
      primaryLabel: "My Jyotishi",
    };
  }
  if (rememberedReading) {
    return {
      accountSession: null,
      authSubject,
      rememberedReading,
      hasPrivateAccountAccess: false,
      primaryHref: readingHref(rememberedReading),
      primaryLabel: "My Reading",
    };
  }
  return {
    accountSession: null,
    authSubject,
    rememberedReading: null,
    hasPrivateAccountAccess: false,
    primaryHref: "/account/",
    primaryLabel: "My Jyotishi",
  };
}
